import { DialogComponent } from '@theme/dialog';
import { formatMoney } from '@theme/money-formatting';

/**
 * <variant-color-modal>
 *
 * A responsive color-picker modal (bottom sheet on mobile, right-side panel on
 * desktop) that extends the theme DialogComponent.
 *
 * Behaviour:
 * - Opens from the "More colors" overflow button in the matching color fieldset.
 * - Lists ALL colors of the option (read from the fieldset's radio inputs).
 * - Selecting a color only updates the modal preview (image, name, price,
 *   "price for X m"); the product variant is NOT changed yet (deferred).
 * - Pressing "Choose" commits the pending color to the variant picker (checks
 *   the matching radio + dispatches change) and closes.
 * - Closing via the close button / backdrop / Escape discards the pending color.
 */
class VariantColorModal extends DialogComponent {
  /** @type {string|null} */
  #pending = null;

  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#wireTrigger();
    this.#bindSlider();
    // Reset the trigger's expanded state whenever the dialog closes
    // (close button, backdrop click, or Escape).
    this.addEventListener(
      'dialog:close',
      () => this.#trigger?.setAttribute('aria-expanded', 'false'),
      { signal: this.#controller.signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  // ─── Element lookups ────────────────────────────────────────────────────────

  get #picker() {
    return this.closest('variant-picker');
  }

  get #fieldset() {
    const idx = this.dataset.fieldsetIndex;
    return (
      this.#picker?.querySelector(`.variant-option--color-circles[data-fieldset-index="${idx}"]`) ?? null
    );
  }

  get #trigger() {
    return /** @type {HTMLElement|null} */ (
      document.querySelector(`[data-color-modal-trigger="${this.id}"]`)
    );
  }

  /**
   * The modal can be rendered in a normal product section or in the quick-add
   * dialog. Keep product-control lookups inside that local context so a cart
   * drawer (or another product section) can never supply its quantity.
   */
  get #productContext() {
    return this.closest('#quick-add-modal-content') ?? this.#picker?.closest('.shopify-section') ?? null;
  }

  /**
   * Returns the form for the same product as this modal's variant picker.
   */
  get #productForm() {
    const productId = this.#picker?.dataset.productId;
    const context = this.#productContext;
    if (!context || !productId) return null;

    return (
      [...context.querySelectorAll('product-form-component')].find(
        (form) => form.getAttribute('data-product-id') === productId
      ) ?? null
    );
  }

  /** @returns {HTMLInputElement[]} All color radio inputs for this fieldset. */
  #colorInputs() {
    const fs = this.#fieldset;
    if (!fs) return [];
    return /** @type {HTMLInputElement[]} */ ([...fs.querySelectorAll('input[type="radio"]')]);
  }

  // ─── Trigger wiring ─────────────────────────────────────────────────────────

  #wireTrigger() {
    const trigger = this.#trigger;
    if (!trigger) return;
    trigger.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        this.open();
      },
      { signal: this.#controller.signal }
    );
  }

  // ─── Open / preview ─────────────────────────────────────────────────────────

  open() {
    const inputs = this.#colorInputs();
    if (!inputs.length) return;

    const checked = inputs.find((input) => input.checked) ?? inputs[0];
    this.#pending = checked?.value ?? null;

    this.#renderColors();
    this.#updatePreview();

    this.#trigger?.setAttribute('aria-expanded', 'true');
    this.showDialog();

    // Reset slider to the first slide once the dialog is visible.
    requestAnimationFrame(() => this.#resetSlider());
  }

  /**
   * Renders the color circle buttons from the fieldset radio inputs.
   */
  #renderColors() {
    const container = this.querySelector('[data-modal-colors]');
    if (!(container instanceof HTMLElement)) return;

    container.innerHTML = '';

    for (const input of this.#colorInputs()) {
      const data = this.#dataFor(input);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'variant-color-modal__color';
      btn.dataset.value = data.value;
      btn.setAttribute('aria-label', data.name);
      btn.setAttribute('aria-pressed', String(data.value === this.#pending));
      if (data.available === 'false') btn.classList.add('is-unavailable');

      const circle = document.createElement('span');
      circle.className = 'variant-color-modal__color-circle';
      if (data.hex) circle.style.backgroundColor = data.hex;
      btn.appendChild(circle);

      btn.addEventListener(
        'click',
        () => {
          this.#pending = data.value;
          this.#syncActiveColor();
          this.#updatePreview();
        },
        { signal: this.#controller.signal }
      );

      container.appendChild(btn);
    }
  }

  #syncActiveColor() {
    const buttons = this.querySelectorAll('.variant-color-modal__color');
    buttons.forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      btn.setAttribute('aria-pressed', String(btn.dataset.value === this.#pending));
    });
  }

  /**
   * @param {HTMLInputElement} input
   */
  #dataFor(input) {
    const label = input.closest('label');
    const circle = label?.querySelector('.variant-option__color-circle');
    const circleHex =
      circle instanceof HTMLElement ? circle.style.backgroundColor : '';

    return {
      value: input.value,
      name: (input.getAttribute('aria-label') || input.value).split(' - ')[0],
      hex: input.dataset.colorHex || circleHex,
      priceCents: input.dataset.priceCents || '',
      priceMoney: input.dataset.priceMoney || '',
      compareCents: input.dataset.compareCents || '',
      compareMoney: input.dataset.compareMoney || '',
      variantMedia: input.dataset.variantMedia || '',
      available: input.dataset.optionAvailable || 'true',
    };
  }

  #updatePreview() {
    const input = this.#colorInputs().find((i) => i.value === this.#pending);
    if (!input) return;
    const data = this.#dataFor(input);
    const qty = this.#quantity();

    const nameEl = this.querySelector('[data-modal-color-name]');
    if (nameEl) nameEl.textContent = data.name;

    const priceEl = this.querySelector('[data-modal-price]');
    if (priceEl) priceEl.textContent = this.#money(data.priceCents, qty) || data.priceMoney;

    const compareEl = this.querySelector('[data-modal-compare]');
    if (compareEl instanceof HTMLElement) {
      const compare = this.#money(data.compareCents, qty) || data.compareMoney;
      compareEl.textContent = compare;
      compareEl.hidden = !compare;
    }

    this.#updateVariantImage(data.variantMedia);
    this.#updatePriceFor(input);

    // Scroll back to the first slide so the (swapped) variant image is visible.
    this.#resetSlider();
  }

  /**
   * Formats `cents * qty` using the shop money format. Returns '' when the value
   * or the money format is unavailable (caller falls back to the per-item money).
   * @param {string} cents
   * @param {number} qty
   */
  #money(cents, qty) {
    const format = this.dataset.moneyFormat;
    const value = parseInt(cents, 10);
    if (!format || !Number.isFinite(value)) return '';
    return formatMoney(value * qty, format, this.dataset.currency || '');
  }

  #resetSlider() {
    const slides = this.querySelector('[data-modal-slides]');
    if (slides instanceof HTMLElement) slides.scrollTo({ left: 0, behavior: 'instant' });
    this.#updateProgress();
  }

  /**
   * @param {string} url
   */
  #updateVariantImage(url) {
    const slide = this.querySelector('[data-modal-variant-slide]');
    const img = this.querySelector('[data-modal-variant-img]');
    if (!(slide instanceof HTMLElement) || !(img instanceof HTMLImageElement)) return;

    if (url) {
      img.src = url;
      slide.hidden = false;
    } else {
      img.removeAttribute('src');
      slide.hidden = true;
    }
  }

  /**
   * Shows "price for X m" where X is the total configured width in meters
   * (quantity × per-unit width). Hidden when there is no width option.
   * @param {HTMLInputElement} input
   */
  #updatePriceFor(input) {
    const el = this.querySelector('[data-modal-price-for]');
    if (!(el instanceof HTMLElement)) return;

    const unit = this.#unitWidthCm();
    if (!unit) {
      el.hidden = true;
      return;
    }

    const qty = this.#quantity();
    const meters = (qty * unit) / 100;
    const prefix = this.dataset.pricePrefix || '';
    el.textContent = `${prefix} ${this.#formatMeters(meters)} m`.trim();
    el.hidden = false;
  }

  #quantity() {
    const input = this.#productForm?.querySelector('[ref="quantityInput"]');
    const value = input instanceof HTMLInputElement ? parseFloat(input.value) : NaN;
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  #unitWidthCm() {
    const el = this.#productForm?.querySelector('quantity-total-display');
    const unit = el instanceof HTMLElement ? parseFloat(el.dataset.unit ?? '') : NaN;
    return Number.isFinite(unit) && unit > 0 ? unit : 0;
  }

  /**
   * @param {number} meters
   */
  #formatMeters(meters) {
    return String(Math.round(meters * 100) / 100).replace('.', ',');
  }

  // ─── Slider ─────────────────────────────────────────────────────────────────

  #bindSlider() {
    const slides = this.querySelector('[data-modal-slides]');
    if (!(slides instanceof HTMLElement)) return;
    slides.addEventListener('scroll', () => this.#updateProgress(), {
      passive: true,
      signal: this.#controller.signal,
    });

    // Native overflow-x scrolling only responds to touch/trackpad swipes, not to
    // a mouse click-and-drag. Add a lightweight drag-to-scroll for mouse users
    // so desktop visitors can scroll the slider the same way.
    this.#bindMouseDragScroll(slides);
  }

  /**
   * @param {HTMLElement} slides
   */
  #bindMouseDragScroll(slides) {
    const { signal } = this.#controller;

    slides.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;

        // Stop the browser's native image-drag / text-selection gesture from
        // hijacking the pointer — without this, dragging over a slide's <img>
        // starts a ghost-image drag instead of firing our pointermove handler.
        event.preventDefault();

        const startX = event.clientX;
        const startScrollLeft = slides.scrollLeft;
        let dragged = false;

        // Pointer velocity in px/second at the moment of release (positive =
        // moving right), used to detect a "flick" and animate to the next/
        // previous slide instead of just the nearest one.
        let velocity = 0;
        let lastX = startX;
        let lastTime = performance.now();

        // Batch scrollLeft writes to one per animation frame so the drag
        // tracks the cursor smoothly instead of jittering on every pointermove.
        let rafId = 0;
        let pendingScrollLeft = startScrollLeft;
        const flushScroll = () => {
          rafId = 0;
          slides.scrollLeft = pendingScrollLeft;
        };

        slides.setPointerCapture(event.pointerId);
        slides.classList.add('is-dragging');

        const dragController = new AbortController();

        slides.addEventListener(
          'pointermove',
          (moveEvent) => {
            const current = moveEvent.clientX;
            const delta = current - startX;
            if (Math.abs(delta) > 3) dragged = true;

            const now = performance.now();
            const timeDelta = now - lastTime;
            if (timeDelta > 0) {
              velocity = ((current - lastX) / timeDelta) * 1000;
            }
            lastX = current;
            lastTime = now;

            pendingScrollLeft = startScrollLeft - delta;
            if (!rafId) rafId = requestAnimationFrame(flushScroll);
          },
          { signal: dragController.signal }
        );

        const stopDragging = () => {
          dragController.abort();
          if (rafId) cancelAnimationFrame(rafId);
          slides.classList.remove('is-dragging');
          if (slides.hasPointerCapture(event.pointerId)) {
            slides.releasePointerCapture(event.pointerId);
          }

          if (dragged) {
            // Animate a smooth "swipe" settle to the nearest slide (or the
            // next/previous one on a fast flick), like native touch momentum.
            this.#settleSlide(slides, velocity);
            // Swallow the click that follows a real drag so it doesn't trigger
            // whatever the pointerdown landed on (e.g. a slide/link).
            slides.addEventListener('click', (clickEvent) => clickEvent.preventDefault(), {
              once: true,
              capture: true,
            });
          }
        };

        slides.addEventListener('pointerup', stopDragging, { signal: dragController.signal });
        slides.addEventListener('pointercancel', stopDragging, { signal: dragController.signal });
      },
      { signal }
    );
  }

  /**
   * After a mouse drag ends, animate a smooth glide to the nearest slide, or
   * to the next/previous one when released on a fast "flick" — mimicking
   * native touch momentum scrolling instead of an abrupt scroll-snap jump.
   * @param {HTMLElement} slides
   * @param {number} velocity - Pointer velocity in px/second at release (positive = moving right).
   */
  #settleSlide(slides, velocity) {
    const items = /** @type {HTMLElement[]} */ (
      [...slides.querySelectorAll('.variant-color-modal__slide')].filter((slide) => !slide.hidden)
    );
    if (!items.length) return;

    const slideWidth = items[0].getBoundingClientRect().width || slides.clientWidth;
    if (!slideWidth) return;

    const currentIndex = Math.round(slides.scrollLeft / slideWidth);

    const FLICK_THRESHOLD = 400; // px/second
    let targetIndex = currentIndex;
    if (velocity > FLICK_THRESHOLD) targetIndex -= 1;
    else if (velocity < -FLICK_THRESHOLD) targetIndex += 1;

    targetIndex = Math.max(0, Math.min(items.length - 1, targetIndex));

    slides.scrollTo({ left: items[targetIndex].offsetLeft, behavior: 'smooth' });
  }

  #updateProgress() {
    const slides = this.querySelector('[data-modal-slides]');
    const fill = this.querySelector('[data-modal-progress]');
    if (!(slides instanceof HTMLElement) || !(fill instanceof HTMLElement)) return;

    // Fill represents the visible portion: grows from (one slide / total) to 100%,
    // advancing by roughly one step per slide scrolled.
    const total = slides.scrollWidth;
    const ratio = total > 0 ? (slides.scrollLeft + slides.clientWidth) / total : 1;
    fill.style.width = `${Math.min(100, Math.max(0, ratio * 100))}%`;
  }

  // ─── Commit ─────────────────────────────────────────────────────────────────

  choose = async () => {
    const pending = this.#pending;
    await this.closeDialog();
    if (pending == null) return;
    this.#commit(pending);
  };

  /**
   * @param {string} value
   */
  #commit(value) {
    const input = this.#colorInputs().find((i) => i.value === value);
    if (!input || input.checked) return;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('variant-color-modal')) {
  customElements.define('variant-color-modal', VariantColorModal);
}
